import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../logo_colored_transp_no_text.png'; 
import LoginForm from './LoginForm'; 
import { FiMenu, FiX } from 'react-icons/fi';

import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from '../firebase';
import { useTranslation } from '../context/LanguageContext';

// --- ROBUST INLINE SVG FLAGS ---
const HungaryFlagSvg = () => (
  <svg width="20" height="14" viewBox="0 0 900 600" style={{ borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', verticalAlign: 'middle', display: 'inline-block' }}>
    <rect width="900" height="200" fill="#ce2939" />
    <rect y="200" width="900" height="200" fill="#fff" />
    <rect y="400" width="900" height="200" fill="#477050" />
  </svg>
);

const UkFlagSvg = () => (
  <svg width="20" height="14" viewBox="0 0 60 30" style={{ borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', verticalAlign: 'middle', display: 'inline-block' }}>
    <clipPath id="s">
      <path d="M0,0 v30 h60 v-30 z"/>
    </clipPath>
    <clipPath id="t">
      <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/>
    </clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

const Navbar = () => {
  const { locale, changeLanguage, t } = useTranslation();
  const [showLogin, setShowLogin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null); 
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') === 'true') {
      setShowLogin(true);
    }
  }, [location]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('resize', handleResize);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);

        const savedRole = localStorage.getItem('archery_nexus_role');
        if (savedRole) {
          setUserData({
            displayName: savedRole === 'club' ? "Irányítópult" : t('nav.myProfile'),
            type: savedRole
          });
        }

        try {
          if (navigator.onLine) {
            const clubRef = doc(db, "Clubs", user.uid);
            const clubSnap = await getDoc(clubRef);

            if (clubSnap.exists()) {
              const data = clubSnap.data();
              const role = 'club';
              localStorage.setItem('archery_nexus_role', role);
              setUserData({
                ...data,
                displayName: "Irányítópult",
                type: role
              });
            } else {
              const userRef = doc(db, "users", user.uid);
              const userSnap = await getDoc(userRef);

              if (userSnap.exists()) {
                const data = userSnap.data();
                const isClub = data.role?.toLowerCase() === 'club';
                const role = isClub ? 'club' : 'competitor';
                localStorage.setItem('archery_nexus_role', role);

                setUserData({
                  ...data,
                  displayName: isClub
                    ? "Irányítópult"
                    : (data.name || data.displayName || t('nav.myProfile')),
                  type: role
                });
              }
            }
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          if (!userData) {
            setUserData({ displayName: t('nav.myProfile'), type: 'unknown' });
          }
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
        localStorage.removeItem('archery_nexus_role');
      }
    });

    return () => {
      unsubscribe();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [t]);

  const handleProfileNavigation = () => {
    setMobileMenuOpen(false);
    if (!userData) return;
    if (userData.type === 'club') {
      navigate('/egyesulet-dashboard');
    } else {
      navigate('/profil');
    }
  };

  const handleLogout = async () => {
    if (!navigator.onLine) {
      alert(t('nav.offlineAlert'));
      return;
    }
    setMobileMenuOpen(false);
    try {
      localStorage.removeItem('archery_nexus_role');
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Kijelentkezési hiba:", error);
    }
  };

  const buttonStyle = {
    backgroundColor: 'white',
    color: '#b30000',
    padding: '0.4rem 0.8rem',
    borderRadius: '5px',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    marginLeft: isMobile ? '0' : '10px',
    display: 'inline-block',
    width: isMobile ? '100%' : 'auto',
    textAlign: 'center',
    marginBottom: isMobile ? '10px' : '0',
    boxSizing: 'border-box'
  };

  const userPageButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    border: '1px solid white',
    opacity: userData ? 1 : 0.7,
    cursor: userData ? 'pointer' : 'wait'
  };

  const logoContainerStyle = {
    backgroundColor: '#ffffff',
    colorScheme: 'light',
    padding: '4px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s ease',
    marginLeft: '5px',
    lineHeight: 0,
    overflow: 'hidden'
  };

  const handleMouseEnter = (e, isUserBtn) => {
    if (isMobile) return;
    e.target.style.transform = 'scale(1.05)';
    if (!isUserBtn) e.target.style.backgroundColor = '#f2f2f2';
    else e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
  };

  const handleMouseLeave = (e, isUserBtn) => {
    if (isMobile) return;
    e.target.style.transform = 'scale(1)';
    if (!isUserBtn) e.target.style.backgroundColor = 'white';
    else e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
  };

  return (
    <>
      <nav
        className="no-print"
        style={{
          backgroundColor: '#b30000',
          padding: isMobile ? '0.5rem 1rem' : '0.5rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white',
          fontFamily: 'Arial, sans-serif',
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <div 
            className="force-light-bg"
            style={logoContainerStyle}
            onClick={() => navigate('/')}
          >
            <img
              src={logo}
              alt="Archery Nexus Logó"
              style={{ 
                height: isMobile ? '35px' : '45px',
                width: 'auto', 
                display: 'block',
                marginTop: '4px'
              }} 
            />
          </div>

          <span
            style={{
              fontSize: isMobile ? '1.1rem' : '1.4rem',
              fontWeight: 'bold',
              color: 'white',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            ArcheryNexus
          </span>

          {/* ASZTALI NYELVCSERÉLŐ KUSTOM DROPDOWN SVG ZÁSZLÓKKAL */}
          {!isMobile && (
            <div style={{ position: 'relative', display: 'inline-block', marginLeft: '6px' }}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '4px',
                  padding: '4px 10px',
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  outline: 'none'
                }}
              >
                {locale === 'hu' ? <HungaryFlagSvg /> : <UkFlagSvg />}
                <span>{locale.toUpperCase()} ▾</span>
              </button>

              {langDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                  zIndex: 2000,
                  minWidth: '95px'
                }}>
                  <div
                    onClick={() => { changeLanguage('hu'); setLangDropdownOpen(false); }}
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      backgroundColor: locale === 'hu' ? '#fff5f5' : 'white',
                      color: '#333',
                      fontSize: '0.9rem',
                      fontWeight: locale === 'hu' ? 'bold' : 'normal'
                    }}
                  >
                    <HungaryFlagSvg /> HU
                  </div>
                  <div
                    onClick={() => { changeLanguage('en'); setLangDropdownOpen(false); }}
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      backgroundColor: locale === 'en' ? '#fff5f5' : 'white',
                      color: '#333',
                      fontSize: '0.9rem',
                      fontWeight: locale === 'en' ? 'bold' : 'normal'
                    }}
                  >
                    <UkFlagSvg /> EN
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ASZTALI MENÜ */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <div style={{ display: 'flex', gap: '1.2rem', fontWeight: 'bold' }}>
              <Link to="/versenyek" style={{ color: 'white', textDecoration: 'none' }}>{t('nav.competitions')}</Link>
              <Link to="/eredmenyek" style={{ color: 'white', textDecoration: 'none' }}>{t('nav.results')}</Link>
              <Link to="/egyesuletek" style={{ color: 'white', textDecoration: 'none' }}>{t('nav.clubs')}</Link>
              <Link to="/kapcsolat" style={{ color: 'white', textDecoration: 'none' }}>{t('nav.contact')}</Link>
              <Link to="/adatvedelem" style={{ color: 'white', textDecoration: 'none' }}>{t('nav.privacy')}</Link>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              {currentUser ? (
                <>
                  <button
                    onClick={handleProfileNavigation}
                    style={userPageButtonStyle}
                    onMouseEnter={(e) => handleMouseEnter(e, true)}
                    onMouseLeave={(e) => handleMouseLeave(e, true)}
                    disabled={!userData}
                  >
                    🏹 {userData ? userData.displayName : t('nav.loading')}
                  </button>

                  {isOnline ? (
                    <button
                      onClick={handleLogout}
                      style={buttonStyle}
                      onMouseEnter={(e) => handleMouseEnter(e, false)}
                      onMouseLeave={(e) => handleMouseLeave(e, false)}
                    >
                      {t('nav.logout')}
                    </button>
                  ) : (
                    <div style={{
                      ...buttonStyle,
                      backgroundColor: 'transparent',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.4)',
                      cursor: 'default',
                      fontSize: '0.75rem',
                      display: 'flex',
                      flexDirection: 'column',
                      lineHeight: '1.2',
                      padding: '4px 10px'
                    }}>
                      <span>{t('nav.offlineMode')}</span>
                      <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>{t('nav.logoutDisabled')}</span>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => {
                    sessionStorage.removeItem('redirectAfterLogin');
                    sessionStorage.removeItem('onlyAthlete');
                    setShowLogin(true);
                  }}
                  style={buttonStyle}
                  onMouseEnter={(e) => handleMouseEnter(e, false)}
                  onMouseLeave={(e) => handleMouseLeave(e, false)}
                >
                  {t('nav.login')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* MOBIL HAMBURGER GOMB */}
        {isMobile && (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '5px' }}
          >
            {mobileMenuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
          </button>
        )}
      </nav>

      {/* MOBIL LENYÍLÓ MENÜ */}
      {isMobile && mobileMenuOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              top: isMobile ? '55px' : '65px',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 998,
              backdropFilter: 'blur(2px)'
            }}
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className="mobile-menu-light"
            style={{
              position: 'fixed',
              top: isMobile ? '55px' : '65px',
              right: 0,
              bottom: 0,
              width: '80%',
              zIndex: 999,
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              backgroundColor: '#fff',
              boxShadow: '-5px 0 15px rgba(0,0,0,0.2)',
              animation: 'slideInRight 0.3s ease-out'
            }}
          >
            <Link to="/versenyek" onClick={() => setMobileMenuOpen(false)} style={{ color: '#333', textDecoration: 'none', fontSize: '1.1rem', fontWeight: 'bold', padding: '15px 0', borderBottom: '1px solid #ddd' }}>
              {t('nav.competitions')}
            </Link>
            <Link to="/eredmenyek" onClick={() => setMobileMenuOpen(false)} style={{ color: '#333', textDecoration: 'none', fontSize: '1.1rem', fontWeight: 'bold', padding: '15px 0', borderBottom: '1px solid #ddd' }}>
              {t('nav.results')}
            </Link>
            <Link to="/kapcsolat" onClick={() => setMobileMenuOpen(false)} style={{ color: '#333', textDecoration: 'none', fontSize: '1.1rem', fontWeight: 'bold', padding: '15px 0', borderBottom: '1px solid #ddd' }}>
              {t('nav.contact')}
            </Link>
            <Link to="/adatvedelem" onClick={() => setMobileMenuOpen(false)} style={{ color: '#333', textDecoration: 'none', fontSize: '1.1rem', fontWeight: 'bold', padding: '15px 0', borderBottom: '1px solid #ddd' }}>
              {t('nav.privacy')}
            </Link>

            {/* MOBIL NYELV VÁLASZTÓ GOMBOK SVG ZÁSZLÓKKAL */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', borderBottom: '1px solid #ddd', fontSize: '1.1rem', fontWeight: 'bold', color: '#333' }}>
              <span>{t('nav.language')}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => changeLanguage('hu')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: locale === 'hu' ? '2px solid #b30000' : '1px solid #ccc',
                    backgroundColor: locale === 'hu' ? '#fff5f5' : '#f8f9fa',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '0.85rem'
                  }}
                >
                  <HungaryFlagSvg /> HU
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: locale === 'en' ? '2px solid #b30000' : '1px solid #ccc',
                    backgroundColor: locale === 'en' ? '#fff5f5' : '#f8f9fa',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '0.85rem'
                  }}
                >
                  <UkFlagSvg /> EN
                </button>
              </div>
            </div>

            <div style={{ marginTop: '30px' }}>
              {currentUser ? (
                <>
                  <button
                    onClick={handleProfileNavigation}
                    style={{ ...userPageButtonStyle, backgroundColor: '#b30000', color: 'white', border: 'none', marginBottom: '15px' }}
                  >
                    🏹 {userData ? userData.displayName : t('nav.myProfile')}
                  </button>

                  {isOnline ? (
                    <button
                      onClick={handleLogout}
                      style={{ ...buttonStyle, backgroundColor: '#eee', color: '#333', border: '1px solid #ccc' }}
                    >
                      {t('nav.logout')}
                    </button>
                  ) : (
                    <div style={{
                      color: '#666',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      padding: '10px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '5px',
                      border: '1px solid #ddd'
                    }}>
                      {t('nav.offlineMode')} - {t('nav.logoutDisabled')}
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => {
                    sessionStorage.removeItem('redirectAfterLogin');
                    sessionStorage.removeItem('onlyAthlete');
                    setShowLogin(true);
                    setMobileMenuOpen(false);
                  }}
                  style={{ ...buttonStyle, backgroundColor: '#b30000', color: 'white' }}
                >
                  {t('nav.login')}
                </button>
              )}

              <div style={{ marginTop: '10px' }}>
                <a
                  href="https://www.buymeacoffee.com/ArcheryNexus"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...buttonStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    backgroundColor: '#FFDD00',
                    color: '#000000',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    borderRadius: '5px'
                  }}
                >
                  {t('nav.buyCoffee')}
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      {showLogin && !isMobile && <LoginForm onClose={() => setShowLogin(false)} />}

      {showLogin && isMobile && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '20px', backdropFilter: 'blur(3px)'
          }}
          onClick={() => setShowLogin(false)}
        >
          <div style={{ width: '100%', maxWidth: '400px' }}>
            <LoginForm isEmbedded={true} onClose={() => setShowLogin(false)} />
            <button
              onClick={() => setShowLogin(false)}
              style={{
                width: '100%', marginTop: '15px', padding: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)', color: 'white',
                border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px',
                fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              {t('nav.close')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styleStyle = document.createElement("style");
styleStyle.innerText = `
  @keyframes slideInRight {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
`;
document.head.appendChild(styleStyle);

export default Navbar;
